import { useSelector } from "@xstate/react";
import { first, isNull, values } from "lodash";
import { preCheckoutActor, submitBookingActor } from "machines/NewOrderMachine/newOrder.actors";
import NewOrderMachineContext from "machines/NewOrderMachine/newOrder.context";
import { useCallback, useContext, useMemo } from "react";
import { createActor, toPromise } from "xstate";
import { NEW_ORDER_FORM_STEPS } from "../../../constants";

const actorWrapper = async (actor, input) => {
  const actorRef = createActor(actor, { input });
  actorRef.start();
  return toPromise(actorRef).then((res) => {
    actorRef.stop();
    return res;
  });
};

const useNewOrderMachine = () => {
  const machineRef = useContext(NewOrderMachineContext);
  const snapshot = useSelector(machineRef, (state) => state);
  const { context } = snapshot;

  const isCheckoutInitiated = useMemo(() => !isNull(context.checkout.id), [context.checkout.id]);
  const isAlreadyCreated = useMemo(() => !!first(context.references)?.id, [context.references]);
  const step = useMemo(() => values(NEW_ORDER_FORM_STEPS).find((currStep) => snapshot.matches(currStep)), [snapshot]);

  const onGoToStep = useCallback(
    (newStep) => {
      !context.checkout.error && machineRef.send({ type: "GO_TO_STEP", step: newStep });
    },
    [context.checkout.error, machineRef]
  );
  const onToggleFormDirty = useCallback(
    (dirty) => machineRef.send({ type: "TOGGLE_DIRTY", values: dirty }),
    [machineRef]
  );
  const onToggleShouldSaveBooking = useCallback(
    (save) => machineRef.send({ type: "TOGGLE_SHOULD_SAVE_BOOKING", values: save }),
    [machineRef]
  );

  const onResetForm = useCallback(() => machineRef.send({ type: "RESET" }), [machineRef]);
  const onInitializeForm = useCallback(
    (form) => machineRef.send({ type: "INITIALIZE_CONTEXT", values: form }),
    [machineRef]
  );

  const onReinitializeCheckout = useCallback(async () => {
    await actorWrapper(preCheckoutActor, { context }).then((output) => {
      machineRef.send({ type: "REINITIALIZE_CHECKOUT", values: output });
    });
  }, [context, machineRef]);

  const onSubmitBookingForm = useCallback(
    async (form) => {
      const { expressCheckout } = form;

      if (expressCheckout && !isAlreadyCreated) {
        await actorWrapper(submitBookingActor, { event: { values: form }, context }).then((output) => {
          machineRef.send({ type: "CHECKOUT", values: output });
        });
      } else machineRef.send({ type: "SUBMIT", values: form });
    },
    [context, machineRef, isAlreadyCreated]
  );

  const onSubmitStep = useCallback(
    (form) => {
      if (step === NEW_ORDER_FORM_STEPS.BOOKING) onSubmitBookingForm(form);
      else machineRef.send({ type: "NEXT", values: form });
    },
    [machineRef, onSubmitBookingForm, step]
  );

  return [
    context,
    { step, isAlreadyCreated, isCheckoutInitiated },
    {
      onGoToStep,
      onSubmitStep,
      onResetForm,
      onToggleFormDirty,
      onToggleShouldSaveBooking,
      onReinitializeCheckout,
      onInitializeForm,
    },
  ];
};

export default useNewOrderMachine;
